import { useContext, useMemo } from 'react';
import { useRouter } from 'next/router';

import { SortDirection } from '~/domains/common/enums/sort-direction.enum';
import { useIsTablet } from '~/domains/common/hooks/mediaQueries';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import { ContestFilterContext } from '~/domains/contest/domains/lobby/context/ContestFiltersContext/ContestFiltersContext';
import { getVisibleUserEntriesCount } from '~/domains/contest/domains/common/utils/pickControl';
import { getIsRestrictedInLocation } from '~/domains/contest/domains/common/utils/getIsRestrictedInLocation';
import useGetGamecenterPathCallback from '~/domains/contest/domains/gamecenter/domains/common/hooks/useGetGamecenterPathCallback';
import { SortOptions } from '~/services/contests/enums/contest-sort-options.enum';

import ContestTitleColumn from './columns/ContestTitleColumn';
import ContestEntryColumn from './columns/ContestEntryColumn';
import ContestTagsColumn from './columns/ContestTagsColumn';
import ContestPrizesColumn from './columns/ContestPrizesColumn';
import ContestDeadlineColumn from './columns/ContestDeadlineColumn';
import ContestEntryFeeColumn from './columns/ContestEntryFeeColumn';
import ContestActionsColumn from './columns/ContestActionsColumn';

function useTableLobby(data: Contest[]) {
  const { handleSortBy, sortByOrder, sortBy } = useContext(ContestFilterContext);
  const getContestPath = useGetGamecenterPathCallback();
  const isTablet = useIsTablet();
  const router = useRouter();
  const currentQuery = { ...router.query };
  const URLSortBy = currentQuery[URLQueryKeys.SORT_BY] as SortOptions;
  const URLSortByOrder = currentQuery[URLQueryKeys.SORT_ORDER] as SortDirection;

  // Need to convert to string to match the type of the columnHeaders
  const sortByOrderEnum = URLSortByOrder === SortDirection.ASC ? 'ASC' : 'DESC';

  // Column headers
  const columnHeaders = useMemo(
    () => [
      {
        label: 'Contest',
        key: 'contestTitle',
        sort: URLSortBy === SortOptions.CONTEST_NAME ? sortByOrderEnum : undefined,
        style: {
          width: '99%',
          minWidth: '400px',
        },
      },
      {
        label: 'Details',
        key: 'details',
        style: {
          minWidth: '100px',
        },
      },
      {
        label: 'Entries',
        key: 'entries',
        sort: URLSortBy === SortOptions.ENTRIES_REMAINING ? sortByOrderEnum : undefined,
        // Disabling for now, until we figure out how to handle sorts from two places
        // onClick: () => handleSortBy(SortOptions.ENTRIES_REMAINING),
        style: {
          minWidth: isTablet ? '100px' : '200px',
        },
      },
      {
        label: 'Total Prizes',
        key: 'totalPrizes',
        sort: URLSortBy === SortOptions.TOTAL_PRIZES ? sortByOrderEnum : undefined,
        // onClick: () => handleSortBy(SortOptions.TOTAL_PRIZES),
        style: {
          minWidth: '120px',
        },
      },
      {
        label: 'Entry Deadline',
        key: 'entryDeadline',
        sort: URLSortBy === SortOptions.ENTRY_DEADLINE_DATE ? sortByOrderEnum : undefined,
        // onClick: () => handleSortBy(SortOptions.ENTRY_DEADLINE_DATE),
        style: {
          minWidth: '120px',
        },
      },
      {
        label: 'Entry Fee',
        key: 'entryFee',
        sort: URLSortBy === SortOptions.ENTRY_FEE ? sortByOrderEnum : undefined,
        // onClick: () => handleSortBy(SortOptions.ENTRY_FEE),
        style: {
          minWidth: '80px',
        },
      },
      {
        label: '',
        key: 'secondaryAction',
        style: {
          minWidth: '50px',
        },
      },
    ],
    [URLSortBy, sortByOrder, handleSortBy, isTablet]
  );

  const rows = useMemo(
    () =>
      !data || data.length === 0
        ? []
        : data.map((contest) => {
            const contestPath = getContestPath({
              contestId: contest.id,
              contestType: contest.contest_type,
              sport: contest.league.sport,
              contestStatus: contest.status,
              userEntriesCount: getVisibleUserEntriesCount(contest),
            });

            return {
              contestTitle: <ContestTitleColumn contest={contest} />,
              details: <ContestTagsColumn contest={contest} />,
              entries: <ContestEntryColumn contest={contest} />,
              totalPrizes: (
                <ContestPrizesColumn
                  prize_pool={contest.prize_pool}
                  resizing_mode={contest.resizing_mode}
                  contest={contest}
                />
              ),
              entryDeadline: <ContestDeadlineColumn start_date={contest.start_date} />,
              entryFee: <ContestEntryFeeColumn entry_fee={contest.entry_fee} />,
              secondaryAction: <ContestActionsColumn contest={contest} contestPath={contestPath} />,
              _rowClick: () => {
                if (!getIsRestrictedInLocation(contest)) {
                  router.push(contestPath);
                }
              },
            };
          }),
    [data, getContestPath]
  );

  const cellStyles = useMemo(
    () =>
      data?.reduce(
        (acc, contest, index) => {
          const cellStyle = getIsRestrictedInLocation(contest)
            ? { opacity: 0.5, cursor: 'not-allowed' }
            : { opacity: 1, cursor: 'pointer' };

          acc[index] = {
            0: cellStyle, // contestTitle
            1: cellStyle, // details
            2: cellStyle, // entries
            3: cellStyle, // totalPrizes
            4: cellStyle, // entryDeadline
            5: cellStyle, // entryFee
            6: cellStyle, // secondaryAction
          };

          return acc;
        },
        {} as Record<number, Record<number, React.CSSProperties>>
      ),
    [data]
  );

  return {
    columnHeaders,
    rows,
    cellStyles,
  };
}

export default useTableLobby;
